// @ts-nocheck
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { IoMdAddCircle } from 'react-icons/io';
import { AppButton } from '@/components/atoms';
import { useDebounce } from '@/hooks/useDebounce';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';

import { DataTable } from '@/components/organism/Table';
import FullPageCard from '@/components/organism/Cards/FullPageCard';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import GlobalSearch from '@/components/atoms/GlobalSearch';
import SegmentedControl from '@/components/elements/SegmentedControl';
import ConfirmActionModal, { ActivateIcon } from '@/components/organism/ConfirmActionModal';
import {
  PROVIDER_CONSTANT_PROPS,
  PROVIDER_VIEW_PROPS,
} from '@/constant/provider/provider';
import { getProviderList } from '@/store/selectors/providers.selector';
import {
  getProviders,
  setShouldRefetchProviders,
  activateProvider,
} from '@/store/slices/provider.slice';
import {
  ROW_PER_PAGE_OPTION,
  authorizeRole,
  STRINGS,
  pagination as PAGINATION_INITIAL_STATE,
  SortEnum,
} from '@/constant/global/global.constant';
import { getRole, ROLES } from '@/constant/users/user.constant';
import type { Provider } from '@/store/types';
import { NAV_ITEMS, getNavItemIcon } from '@/constant/layout/layout.constant';
import { tid } from '@/utils/testId';
import { PROVIDER_TEST_IDS as TID } from '@/constant/testIds/provider.testIds';
import { buildColumns } from './provider.columns';

/* ── Component ────────────────────────────────────────────────────────────── */
const ProviderComponent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [activateTarget, setActivateTarget] = useState<Provider | null>(null);
  const { ACTIVATE_MODAL } = PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS
  const isAdmin = getRole() === ROLES.ADMIN;
  const [isCopy, setIsCopy] = useState('');
  const isCopyRef = useRef('');
  const [search, setSearch] = useState('');
  const [searchByPartnerId, setSearchByPartnerId] = useState('');
  const [activeStatus, setActiveStatus] = useState<string>('1');
  const [pagination, setPagination] = useState(PAGINATION_INITIAL_STATE);
  const [sorting, setSorting] = useState({
    orderBy: 'updatedOn',
    order: 'desc' as SortEnum,
  });

  const nameQuery = useDebounce(search, PROVIDER_CONSTANT_PROPS.GENERIC.DELAY);
  const partnerIdQuery = useDebounce(
    searchByPartnerId,
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );

  const userHasWriteAccess = authorizeRole.USERS.CREATE.includes(getRole());

  const {
    loading,
    providers: { data, pagination: paginationResponse },
    shouldRefetchProviders,
  } = useSelector(getProviderList);

  const totalPages = Math.ceil(
    (paginationResponse?.totalRecords || 0) / (pagination.limit || 10),
  );

  // hasLoaded ref — tracks whether we've ever received data
  const hasLoaded = useRef(false);

  const onActivate = useCallback((item: Provider) => setActivateTarget(item), []);

  const onCopyClick = useCallback((key: string) => {
    isCopyRef.current = key;
    setIsCopy(key);
    setTimeout(() => {
      isCopyRef.current = '';
      setIsCopy('');
    }, 500);
  }, []);

  const handlePageSizeChange = useCallback(
    (value: number) => {
      setPagination((p) => ({
        ...p,
        limit: value,
        page: totalPages < p.page ? totalPages : p.page,
      }));
    },
    [totalPages],
  );

  const handlePreviousClick = useCallback((page: number) => {
    if (page > 1) setPagination((p) => ({ ...p, page: page - 1 }));
  }, []);

  const handleNextClick = useCallback(
    (page: number) => {
      if (page < totalPages) setPagination((p) => ({ ...p, page: page + 1 }));
    },
    [totalPages],
  );

  // Track first successful data load
  useEffect(() => {
    if (!hasLoaded.current && Array.isArray(data) && data.length > 0) {
      hasLoaded.current = true;
    }
  }, [data]);

  // Reset to page 1 if current page overshoots
  useEffect(() => {
    if (pagination?.page > totalPages && totalPages !== 0) {
      setPagination((p) => ({ ...p, page: 1 }));
    }
  }, [totalPages]);

  // Main fetch effect
  useEffect(() => {
    if (nameQuery?.trim().length > 0 && nameQuery?.trim().length < 3) return;
    if (partnerIdQuery?.trim().length > 0 && partnerIdQuery?.trim().length < 3)
      return;

    dispatch(
      getProviders({
        ...pagination,
        ...sorting,
        ...(nameQuery?.trim().length >= 3
          ? { keywords: nameQuery.trim() }
          : {}),
        ...(partnerIdQuery?.trim().length >= 3
          ? { partnerId: partnerIdQuery.trim() }
          : {}),
        includeInactive: activeStatus === 'all' || activeStatus === '0' ? true : undefined,
        inactive: activeStatus === '0' ? true : undefined,
      }),
    );
  }, [
    pagination?.page,
    pagination?.limit,
    sorting?.orderBy,
    sorting?.order,
    nameQuery,
    partnerIdQuery,
    activeStatus,
  ]);

  // Re-fetch after create / update / delete
  useEffect(() => {
    if (shouldRefetchProviders) {
      dispatch(getProviders({ ...pagination, ...sorting }));
      dispatch(setShouldRefetchProviders(false));
      setActivateTarget(null);
    }
  }, [shouldRefetchProviders]);

  const columns = useMemo(
    () => buildColumns(isCopyRef, onCopyClick, onActivate, isAdmin),
    [onCopyClick, onActivate, isAdmin],
  );  
  return (
    <FullPageCard
      testId={tid(TID.PAGE)}
      className="rounded-b-none"
      header={
        <PageToolbar
          title={STRINGS.PROVIDER}
          subtitle={STRINGS.PROVIDER_SUB}
          icon={getNavItemIcon(NAV_ITEMS.PROVIDERS)}
          actionsSlot={
            <div className="flex items-center gap-2">
              <GlobalSearch
                className="w-[200px]"
                placeholder={PROVIDER_VIEW_PROPS.LIST.SEARCH_BY_NAME}
                dataTestId={tid(TID.SEARCH_NAME)}
                globalSearch={search}
                setGlobalSearch={(v) => {
                  setSearch(v);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
              />
              <GlobalSearch
                className="w-[200px]"
                placeholder={PROVIDER_VIEW_PROPS.LIST.SEARCH_BY_PARTNER_ID}
                dataTestId={tid(TID.SEARCH_PARTNER_ID)}
                globalSearch={searchByPartnerId}
                setGlobalSearch={(v) => {
                  setSearchByPartnerId(v);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
              />
            </div>
          }
          actionButtons={
            userHasWriteAccess ? (
              <AppButton
                data-testid={tid(TID.ADD_BTN)}
                variant="primary"
                icon={<IoMdAddCircle className="h-4 w-4" />}
                onClick={() => navigate('/providers/create')}
              >
                Create Provider
              </AppButton>
            ) : null
          }
          filtersRight={
            <SegmentedControl
              testId={tid(TID.STATUS_FILTER)}
              size="lg"
              shape="square"
              variant="gradient"
              options={[
                { value: 'all', label: PROVIDER_VIEW_PROPS.FILTERS.STATUS_ALL },
                { value: '1', label: PROVIDER_VIEW_PROPS.FILTERS.STATUS_ACTIVE },
                { value: '0', label: PROVIDER_VIEW_PROPS.FILTERS.STATUS_INACTIVE },
              ]}
              value={activeStatus}
              onChange={(v: string) => {
                setActiveStatus(v);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
            />
          }
        />
      }
    >
      <DataTable
        data-testid={tid(TID.TABLE)}
        data={data ?? []}
        columns={columns}
        loading={loading}
        skeletonRows={15}
        onSort={(orderBy, order) =>
          setSorting({ orderBy, order: order as SortEnum })
        }
        sortState={sorting}
        rowKey={(item: any) => item?.providerId ?? ''}
        rowTestId={(item: any, idx: number) =>
          TID.TABLE_ROW(`${idx}`)
        }
        pagination={
          !isEmpty(data)
            ? {
              state: pagination,
              totalItem: paginationResponse?.totalRecords,
              rowsPerPageOptions: ROW_PER_PAGE_OPTION,
              onRowsPerPageChange: handlePageSizeChange,
              onNextClick: handleNextClick,
              onPreviousClick: handlePreviousClick,
            }
            : null
        }
      />
      
      {/* Reactivate confirmation modal */}
      <ConfirmActionModal
        isOpen={!!activateTarget}
        onClose={() => setActivateTarget(null)}
        onConfirm={() => {
          if (activateTarget?.providerId) {
            dispatch(activateProvider(activateTarget as any));
          }
        }}
        title={ACTIVATE_MODAL.TITLE}
        subtitle={ACTIVATE_MODAL.SUBTITLE}
        description={
          <>
            {ACTIVATE_MODAL.BODY_PREFIX}{' '}
            <span className="font-semibold text-gray-900 dark:text-white">
              {activateTarget?.name || activateTarget?.profile}
            </span>
            ?
          </>
        }
        confirmWord={ACTIVATE_MODAL.CONFIRM_WORD}
        placeholder={ACTIVATE_MODAL.PLACEHOLDER}
        inputLabel={ACTIVATE_MODAL.INPUT_LABEL}
        hint={ACTIVATE_MODAL.HINT}
        confirmLabel={ACTIVATE_MODAL.CONFIRM_LABEL}
        confirmIcon={<ActivateIcon />}
        variant="success"
        size="xl"
        icon={<ActivateIcon />}
        loading={loading}
      />
    </FullPageCard>
  );
};

export default ProviderComponent;
